import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../component/Sidebar";
import CollapsibleHeader from "../component/CollapsibleHeader";
import { Key, RefreshCw, Save, Edit3, XCircle } from "lucide-react";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";

const EmployeeUserIdCreated = () => {
  const [dataList, setDataList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [bulkPassword, setBulkPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [tempPassword, setTempPassword] = useState("");

  const token = localStorage.getItem("token");
  const API_URL = "/api/employee-ids";

  const fetchData = async () => {
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDataList(res.data);
    } catch (err) {
      toast.error("Error loading employees");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const normalizedSearch = searchTerm.trim().toUpperCase();
  const filteredData = dataList.filter((emp) => {
    if (!normalizedSearch) return true;
    const employeeId = String(emp.employeeId || "").toUpperCase();
    const employeeName = String(emp.name || "").toUpperCase();
    return employeeId.includes(normalizedSearch) || employeeName.includes(normalizedSearch);
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredData.map((emp) => emp.employeeId));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkGenerate = async () => {
    if (!bulkPassword) return toast.error("Please enter a password first!");

    const message =
      selectedIds.length > 0
        ? `Update password for ${selectedIds.length} selected employees?`
        : "Update password for ALL employees?";

    if (!window.confirm(message)) return;

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/generate-all-passwords`,
        {
          customPassword: bulkPassword,
          targetEmployeeIds: selectedIds.length > 0 ? selectedIds : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Passwords generated successfully!");
      setBulkPassword("");
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (emp) => {
    setEditingRow(emp.employeeId);
    setTempPassword("");
  };

  const saveIndividualPassword = async (emp) => {
    if (!tempPassword) return toast.error("Password cannot be empty");
    try {
      await axios.post(
        `${API_URL}/generate-all-passwords`,
        {
          customPassword: tempPassword,
          targetEmployeeIds: [emp.employeeId],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password updated for ${emp.name}`);
      setEditingRow(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to update individual password");
    }
  };

  return (
    <div className="flex min-h-screen flex-col overflow-hidden md:h-screen md:flex-row">
      <Sidebar />

      <div className="page-panel overflow-y-auto">
        <div className="page-panel p-4">
          <div className="page-sticky-header mb-3 bg-white pb-2">
            <div className="page-title-bar mb-3 flex items-center justify-between">
              <h2 className="text-xl md:text-2xl font-bold text-dorika-blue">
                Employee Password Management
              </h2>
              <button
                type="button"
                onClick={() => window.history.back()}
                className="inline-flex h-8 w-[110px] items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white shadow transition hover:bg-blue-800"
              >
                <FaArrowLeft />
                <span>Back</span>
              </button>
            </div>

            <CollapsibleHeader title="Employee Password Header" contentClassName="mt-3">
              <div className="page-title-bar">
                <div className="flex flex-col gap-4 md:flex-row md:items-end">
                  <div className="field-stack-mobile flex-1">
                    <label>Search Employee</label>
                    <input
                      type="text"
                      placeholder="Search by employee ID or name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                      className="h-8 w-full rounded-xl border-2 border-gray-200 px-3 uppercase outline-none transition-all focus:border-dorika-blue"
                    />
                  </div>

                  <div className="field-stack-mobile flex-1">
                    <label>
                      {selectedIds.length > 0
                        ? `Target: (${selectedIds.length}) Selected`
                        : "Target: All Employees"}
                    </label>
                    <input
                      type="text"
                      placeholder="Enter password to generate..."
                      value={bulkPassword}
                      onChange={(e) => setBulkPassword(e.target.value)}
                      className="h-8 w-full rounded-xl border-2 border-gray-200 px-3 outline-none transition-all focus:border-dorika-blue"
                    />
                  </div>

                  <button
                    onClick={handleBulkGenerate}
                    disabled={loading}
                    className="inline-flex h-8 items-center justify-center gap-2 rounded-xl bg-dorika-blue px-6 font-bold text-white transition-all hover:bg-black disabled:opacity-50"
                  >
                    {loading ? <RefreshCw className="animate-spin" size={18} /> : <Key size={18} />}
                    <span className="whitespace-nowrap">
                      {selectedIds.length > 0 ? "Generate Selected" : "Generate All"}
                    </span>
                  </button>
                </div>
              </div>
            </CollapsibleHeader>
          </div>

          <div className="table-scroll-area rounded-2xl border border-gray-200 bg-white shadow-md">
            <div className="overflow-x-auto">
              <table className="table-fixed-header w-full min-w-[800px] border-collapse text-left">
                <thead className="bg-blue-50 text-[10px] font-bold uppercase text-dorika-blue md:text-xs">
                  <tr>
                    <th className="w-10 border-b p-3 md:p-4">
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={
                          filteredData.length > 0 &&
                          filteredData.every((emp) => selectedIds.includes(emp.employeeId))
                        }
                        className="h-4 w-4 cursor-pointer accent-dorika-orange"
                      />
                    </th>
                    <th className="border-b p-3 md:p-4">Sl No</th>
                    <th className="border-b p-3 md:p-4">Employee ID</th>
                    <th className="border-b p-3 md:p-4">Name</th>
                    <th className="border-b p-3 md:p-4">Designation</th>
                    <th className="border-b p-3 md:p-4">Password Status</th>
                    <th className="border-b p-3 text-center md:p-4">Action</th>
                  </tr>
                </thead>
                <tbody className="text-xs md:text-sm">
                  {filteredData.map((emp, index) => (
                    <tr
                      key={emp.employeeId || index}
                      className={`border-b transition-colors last:border-0 ${
                        selectedIds.includes(emp.employeeId) ? "bg-orange-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="p-3 md:p-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(emp.employeeId)}
                          onChange={() => handleSelectOne(emp.employeeId)}
                          className="h-4 w-4 cursor-pointer accent-dorika-orange"
                        />
                      </td>
                      <td className="p-3 text-gray-500 md:p-4">{index + 1}</td>
                      <td className="p-3 font-semibold text-gray-800 md:p-4">{emp.employeeId}</td>
                      <td className="p-3 font-medium uppercase md:p-4">{emp.name}</td>
                      <td
                        className="max-w-[150px] truncate p-3 text-[10px] text-gray-600 md:p-4 md:text-xs"
                        title={emp.designation}
                      >
                        {emp.designation}
                      </td>
                      <td className="p-3 md:p-4">
                        {editingRow === emp.employeeId ? (
                          <input
                            type="text"
                            placeholder="Set Password"
                            value={tempPassword}
                            onChange={(e) => setTempPassword(e.target.value)}
                            className="h-8 w-full max-w-[140px] rounded border border-dorika-blue px-2 text-xs shadow-inner outline-none"
                            autoFocus
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <span
                              className={`rounded-lg px-2 py-1 text-[10px] font-bold md:px-3 md:text-xs ${
                                emp.password === "NOT GENERATED" || !emp.password
                                  ? "bg-red-50 text-red-500"
                                  : "bg-green-50 text-green-600"
                              }`}
                            >
                              {emp.password === "NOT GENERATED" || !emp.password ? "NONE" : "GENERATED"}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-center md:p-4">
                        {editingRow === emp.employeeId ? (
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => saveIndividualPassword(emp)}
                              className="text-green-600 hover:text-green-800"
                            >
                              <Save size={18} />
                            </button>
                            <button
                              onClick={() => setEditingRow(null)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <XCircle size={18} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(emp)}
                            className="text-dorika-blue transition-colors hover:text-dorika-orange"
                          >
                            <Edit3 size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredData.length === 0 && (
                    <tr>
                      <td colSpan="7" className="p-6 text-center text-sm text-gray-500">
                        No employee found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeUserIdCreated;
